import { useEffect } from "react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import {
  useLocSettings,
  useProductTypeById,
  useSetting
} from "../../hooks/SettingsHooks";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import { useProductLabels, useUserProducts } from "../../hooks/ProductHooks";
import Page from "../../layout/Page";
import ContentSection from "../../components/ContentSection";
import Gallery from "../../components/Gallery";
import { cmsUrl, transformHtml } from "../../utils/CmsUtils";
import SizeFloor from "../../components/SizeFloor";
import Breadcrumbs from "../../components/Breadcrumbs";
import { Col, Row } from "reactstrap";
import ProductSpecification from "../../components/ProductSpecification";
import ProductPurchaseColumn from "../../components/ProductPurchaseColumn";
import ProductDocuments from "../../components/ProductDocuments";
import ProductLabels from "../../components/ProductLabels";
import CareInstructionModal from "../../components/modal/CareInstructionModal";
import ProductSchema from "../../components/ProductSchema";
import IconsOrTagline from "../../components/IconsOrTagline";
import CockpitProductGrid from "../../components/CockpitLayoutComponents/CockpitProductGrid";
import { productIsUnavailable, productPaths } from "../../utils/ProductUtils";
import { filterValidSkus } from "../../utils/ProductsUtils";
import BjelinButton from "../../components/BjelinButton";
import { useUser } from "../../functional-components/UserContext";
import { useLang } from "../../hooks/LangHooks";
import GuideListing from "../../components/Guide/GuideListing";
import { faqBaseUrl, guidesProductUrl, shopUrl } from "../../config/urls";
import { pdsUrl } from "../../config/apiUrls";
import PriceArea from "../../components/ProductPurchaseColumn/PriceArea";
import SalesRegionsArea from "../../components/ProductPurchaseColumn/SalesRegionsArea";
import ProductFaq from "../../components/FAQ/ProductFaq";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import { gtmSendEvent } from "../../utils/GTMUtils";
import ProductStock from "../../components/ProductStock";
import ProductReplacedByArea from "../../components/ProductPurchaseColumn/ProductReplacedByArea";
import ExpandableContent from "../../components/ExpandableContent";
import { uniqBy } from "lodash";
import { cockpitComponent } from "../../components/CockpitLayoutComponents";
import StoryGrid from "../../components/StoryGrid";
import { Flags } from "../../components/Flags";
import CockpitEditLink from "../../components/CockpitEditLink.jsx";
import { getCurrentSite } from "../../utils/SiteUtils";
import {
  getProductCategoryPedigree,
  productCategoryTitle,
  productCategoryUrl,
  useCategoryByProduct,
  useCategoryFromPath
} from "../../utils/ProductCategoryUtils";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import "./style.scss";

const ContentWrapper = ({ title, artId, displayAsOverlay, children }) => {
  return displayAsOverlay ? (
    children
  ) : (
    <Page
      name={"product-page"}
      key={"product-page" + artId}
      title={title}
      transition={"simple"}
    >
      <div style={{ height: "4rem" }} />
      {children}
    </Page>
  );
};

const ProductPageInner2 = ({
  product,
  displayAsOverlay = false,
  location,
  history,
  match
}) => {
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  const lang = useLang();
  const { b2b } = useUser();
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const features = useSiteFeatures();
  const products = useUserProducts();
  const categoryFromProduct = useCategoryByProduct(product);
  const categoryFromPath = useCategoryFromPath(match?.params?.category);
  const category = categoryFromPath || categoryFromProduct;
  const productCategoryPedigree = getProductCategoryPedigree(category);
  const {
    _id: CockpitId,
    name,
    sku,
    productType: productTypeId,
    description,
    images,
    accessories,
    accessoriesReversed
  } = product;

  useEffect(() => {
    const paths = productPaths(product, category);
    registerCurrentPaths(paths);
    setAvailableOnSites(product.sites);
  }, [registerCurrentPaths, setAvailableOnSites, product, category]);

  const title = productIsUnavailable(product)
    ? `${tp("shipping_status.unavailable").toUpperCase()} ${name}`
    : name;

  const productType = useProductTypeById(productTypeId);
  //console.log("productType", productType);

  const backgroundImage =
    category?.is_floor && images[0]
      ? cmsUrl(images[0].path)
      : category?.path === "outlet"
      ? "http://cdn.bjelin.io/storage/uploads/2021/11/19/Outlet-bkg-4-ljus_uid_6197aadbcc504.jpg"
      : false;
  const breadcrumbItems = [
    features.shop()
      ? { url: shopUrl, text: t("shop") }
      : { url: shopUrl, text: t("menu-label.products") }
  ];
  productCategoryPedigree.forEach(cat => {
    breadcrumbItems.push({
      url: productCategoryUrl(cat),
      text: productCategoryTitle(cat, tp)
    });
  });

  const accessoriesToList = filterValidSkus(products, accessories);
  const accessoriesReversedToList = filterValidSkus(
    products,
    accessoriesReversed || []
  );

  return (
    <ContentWrapper
      backgroundImage={backgroundImage}
      title={title}
      artId={sku}
      b2b={b2b}
      displayAsOverlay={displayAsOverlay}
      location={location}
      history={history}
      key={sku}
    >
      <ProductSchema product={product} lang={lang} />
      <ContentSection navTitle={tp("nav-title.images")}>
        <Breadcrumbs items={breadcrumbItems} />
        <HeadingAndTagline product={product} />
        <GalleryColumn product={product} category={category} />
      </ContentSection>
      <ContentSection navTitle={tp("nav-title.buy")}>
        {features.shop() && product.price ? (
          <CartColumn sku={sku} />
        ) : (
          <ContactUsColumn product={product} />
        )}
      </ContentSection>
      {features.shop() && !b2b && accessoriesToList.length > 0 && (
        <ContentSection
          fadeIn
          containerTheme={"grey"}
          className={"mb-3"}
          navTitle={tp("nav-title.accessories")}
        >
          <Row>
            <Col>
              <h3>{t("product.recommended.accessories")}</h3>
              <CockpitProductGrid
                skus={accessoriesToList}
                layout={"list"}
                showCartButtons
                className="accessories-list"
              />
            </Col>
          </Row>
        </ContentSection>
      )}
      <ContentSection navTitle={tp("nav-title.description")}>
        <div className={"product-description"}>
          {transformHtml(description)}
        </div>
      </ContentSection>
      <ContentSection>
        {productType?.product_page_content?.map(cockpitComponent)}
      </ContentSection>
      {features.shop() && !b2b && accessoriesReversedToList.length > 0 && (
        <ContentSection
          fadeIn
          containerTheme={"grey"}
          className={"mb-3"}
          navTitle={tp("nav-title.fits")}
        >
          <Row>
            <Col>
              <h3>{t("product.recommended.reversed.accessories")}</h3>
              <ExpandableContent
                disabled={accessoriesReversedToList.length <= 3}
                backgroundColor="#dfe2e1"
                initialHeight="16.4em"
              >
                <CockpitProductGrid
                  skus={accessoriesReversedToList}
                  layout={"list"}
                  className="accessories-list"
                />
              </ExpandableContent>
            </Col>
          </Row>
        </ContentSection>
      )}
      <ContentSection
        theme={"white"}
        noMargins
        navTitle={tp("nav-title.stories")}
      >
        <StoryGrid
          productCockpitIds={[CockpitId]}
          header={storiesCount =>
            storiesCount ? (
              <ContentSection theme={"white"} style={{ textAlign: "center" }}>
                <h2>{t("stories.related")}</h2>
              </ContentSection>
            ) : null
          }
        />
      </ContentSection>
      <ContentSection navTitle={tp("nav-title.faq")}>
        <ProductFaq product={product} />
      </ContentSection>
      <ContentSection noMargins navTitle={tp("nav-title.specs")}>
        <SpecsColumn product={product} />
      </ContentSection>
    </ContentWrapper>
  );
};

const GalleryColumn = ({ product, category }) => {
  const { sku, images } = product;
  const tp = useProductTranslations();

  return (
    <Gallery
      artId={sku}
      images={images}
      containImage={!category?.is_floor}
      has3DView={product.has_3d_view}
      startIndex={1}
      altText={tp("product-picture-of") + " " + product.name}
    >
      <Flags product={product} inline={false} />
    </Gallery>
  );
};

const HeadingAndTagline = ({ product }) => {
  const { name, sku, specsIndexed, tagline } = product;
  const { b2b } = useUser();

  return (
    <>
      <h1 className={"product-name"}>
        {b2b && <span className="text-pale">{sku} </span>}
        <span>{name}</span>
      </h1>
      <div className={"tagline"}>
        <IconsOrTagline
          specsIndexed={specsIndexed}
          showLabel
          sku={sku}
          tagline={tagline}
        />
      </div>
    </>
  );
};

const CartColumn = ({ sku }) => (
  <ProductPurchaseColumn sku={sku}></ProductPurchaseColumn>
);

const ContactUsColumn = ({ product }) => {
  const { sku } = product;
  const { admin, b2b } = useUser();
  const features = useSiteFeatures();
  const settings = useLocSettings();
  const t = useBjelinTranslations();
  const tp = useProductTranslations();

  return (
    <>
      {features.prices() && <PriceArea product={product} />}
      {features.salesRegions() && <SalesRegionsArea product={product} />}
      <ProductReplacedByArea product={product} />
      <div className={"contact-us-column mb-3"}>
        <h4>{t("product.contact.heading")}</h4>
        <Row>
          {settings?.footer?.contact_email && (
            <Col xl={6} md={12} sm={6} xs={12}>
              <BjelinButton
                onClick={() => {
                  gtmSendEvent({
                    event: "contact",
                    bjelinContactData: {
                      source: "mail-button"
                    }
                  });
                  window.location.href =
                    "mailto:" + settings.footer.contact_email;
                }}
                color={"bjelin-green"}
                block={true}
                className={"mb-2"}
                icon={"envelope-open"}
                icon_position={"before"}
              >
                {settings.footer.contact_email}
              </BjelinButton>
            </Col>
          )}
          {!b2b && (
            <Col xl={6} md={12} sm={6} xs={12}>
              <BjelinButton
                url={"/map"}
                color={"bjelin-green"}
                block={true}
                className={"mb-2"}
                icon={"map-location-dot"}
                icon_position={"before"}
                subtitle={t("product.contact.find-us.subtitle")}
              >
                {t("product.contact.find-us")}
              </BjelinButton>
            </Col>
          )}
        </Row>
        {b2b && (
          <ProductStock
            className={"b2b-catalog-product-stock"}
            product={product}
            b2b={b2b}
          />
        )}
      </div>
      <div className={"product-purchase-column"}>
        <p className={"article-number"}>
          {tp("article_number")}: {sku}
          <CockpitEditLink
            collection={"products"}
            _id={product._id}
            labelKey={" "}
            wrapper={children => children}
          />
          {admin && (
            <a
              rel={"noreferrer"}
              target={"_blank"}
              href={`https://bjel.in/info/${getCurrentSite()}/art/${sku}`}
              className={"text-black ms-1"}
            >
              <Icon name={"qrcode"} />
            </a>
          )}
        </p>
        {admin && product.tags && (
          <p className={"article-tags"}>
            {product.tags.map(tag => (
              <span key={tag} style={{ marginRight: "1em" }}>
                <Icon type={"far"} name={"tag"} /> {tag}
              </span>
            ))}
          </p>
        )}
      </div>
    </>
  );
};

const SpecsColumn = ({ product }) => {
  const {
    sku,
    productType: productTypeId,
    specifications,
    specsGrouped,
    documents,
    care_instruction: careInstructionContent
  } = product;

  const lang = useLang();
  const site = getCurrentSite();
  const features = useSiteFeatures();
  const faq_product_sections = useSetting("faq_product_section");
  const productLabels = useProductLabels(product);
  const productType = useProductTypeById(productTypeId);
  const t = useBjelinTranslations();
  const tp = useProductTranslations();

  const FAQ_PRODUCT_SECTION_ID = faq_product_sections?.[lang];
  const allDocuments = uniqBy(
    documents.concat(productType?.documents || []),
    "_id"
  );
  const hasProductDocuments = (allDocuments || []).length > 0;
  const hasCareInstructions = !!careInstructionContent;
  const hasProductLabels = productLabels.length > 0;

  return (
    <>
      <div className="two-col-layout">
        <div className="col">
          {specifications.length > 0 && (
            <ContentSection
              fadeIn
              containerTheme={"sand"}
              className={"product-specification mb-3"}
            >
              <Row>
                <Col>
                  <div
                    className="cockpit-bjelin_button"
                    style={{ float: "right" }}
                  >
                    <a
                      className="button button-primary"
                      href={pdsUrl(sku, site, lang)}
                      target="_blank"
                      rel="noreferrer"
                      title={t("print.product-document")}
                    >
                      <Icon name="download" />
                    </a>
                  </div>
                  <h3>{tp("specification")}</h3>
                  <ProductSpecification specsGrouped={specsGrouped} />
                  {hasProductLabels && <ProductLabels labels={productLabels} />}
                </Col>
              </Row>
            </ContentSection>
          )}
        </div>
        <div className="col">
          {(hasProductDocuments || hasCareInstructions || hasProductLabels) && (
            <ContentSection
              fadeIn
              containerTheme={"sand"}
              className={"product-specification mb-3"}
            >
              <Row>
                <Col>
                  <h3>
                    {tp(
                      features.guides() ? "guides.and.documents" : "documents"
                    )}
                  </h3>
                  {features.guides() && (
                    <GuideListing
                      tags={product.tags}
                      pathPrefix={guidesProductUrl(sku)}
                    />
                  )}
                  {hasProductDocuments && (
                    <ProductDocuments documents={allDocuments} />
                  )}
                  {hasCareInstructions && (
                    <CareInstructionModal content={careInstructionContent} />
                  )}
                  {hasProductDocuments && FAQ_PRODUCT_SECTION_ID && (
                    <ul className="guide-listing">
                      <li>
                        <Link
                          className={"document"}
                          to={`${faqBaseUrl}/${FAQ_PRODUCT_SECTION_ID}/installation`}
                        >
                          <Icon name="circle-question" />{" "}
                          {t("faq.installation-questions")}
                        </Link>
                      </li>
                    </ul>
                  )}
                  <SizeFloor className={"sizefloor"} product={product} />
                </Col>
              </Row>
            </ContentSection>
          )}
        </div>
      </div>
    </>
  );
};
export default ProductPageInner2;
